import { forwardRef, useImperativeHandle, useState } from 'react';
import type { DeliveryCondition, FallbackAction, LifeEvent } from '../lib/seal/sealApi';

type Mode = 'date' | 'age' | 'life_event';

const LIFE_EVENTS: ReadonlyArray<{ value: LifeEvent; label: string }> = [
  { value: 'birthday_18', label: 'their 18th birthday' },
  { value: 'birthday_21', label: 'their 21st birthday' },
  { value: 'graduation', label: 'the day they graduate' },
  { value: 'wedding', label: 'the day they marry' },
  { value: 'first_child', label: 'the day they have their first child' },
];

const FALLBACKS: ReadonlyArray<{ value: FallbackAction; label: string }> = [
  { value: 'release_unconditional', label: 'send it to them anyway' },
  { value: 'escalate_to_guardians', label: 'ask my guardians what to do' },
  { value: 'notify_creator', label: 'remind me, so I can decide' },
  { value: 'archive', label: 'quietly let it go' },
];

export interface ConditionHandle {
  /** Validate + return the condition, or null (and report why via onError). */
  build(): DeliveryCondition | null;
}

/**
 * The reusable "when should this reach them?" condition builder — a date, an
 * age, or a life event (with an optional "but if it hasn't happened by…"
 * fallback). Extracted from ScheduleView so a compound trigger can render more
 * than one and combine them with AND/OR. Holds its own state; exposes build()
 * imperatively via ref.
 */
export const ConditionFields = forwardRef<
  ConditionHandle,
  { minDate: string; idPrefix: string; onError: (m: string) => void; compoundMember?: boolean }
>(function ConditionFields({ minDate, idPrefix, onError, compoundMember = false }, ref) {
  const [mode, setMode] = useState<Mode>('date');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [age, setAge] = useState('');
  const [lifeEvent, setLifeEvent] = useState<LifeEvent>('birthday_21');
  const [fallbackDate, setFallbackDate] = useState('');
  const [fallbackAction, setFallbackAction] = useState<FallbackAction>('release_unconditional');

  useImperativeHandle(ref, () => ({
    build(): DeliveryCondition | null {
      if (mode === 'age') {
        const n = Number(age);
        if (!Number.isInteger(n) || n < 1 || n > 120) {
          onError('Give an age between 1 and 120.');
          return null;
        }
        return { kind: 'age', age: n };
      }
      if (mode === 'life_event') {
        return {
          kind: 'life_event',
          event: lifeEvent,
          // A compound member gets no fallback — the group's combinator is the
          // "but" (a per-member fallback would fight the AND/OR at delivery).
          ...(!compoundMember && fallbackDate !== '' ? { fallbackDate, fallbackAction } : {}),
        };
      }
      if (deliveryDate === '') {
        onError('Choose a delivery date.');
        return null;
      }
      return { kind: 'date', date: deliveryDate };
    },
  }));

  const name = `${idPrefix}-mode`;
  return (
    <div className="sched__condition">
      <fieldset className="sched__modes">
        <legend className="sched__label">When should it reach them?</legend>
        <label className="sched__mode">
          <input type="radio" name={name} checked={mode === 'date'} onChange={() => setMode('date')} />
          <span>On a certain day</span>
        </label>
        <label className="sched__mode">
          <input type="radio" name={name} checked={mode === 'age'} onChange={() => setMode('age')} />
          <span>When they reach a certain age</span>
        </label>
        <label className="sched__mode">
          <input type="radio" name={name} checked={mode === 'life_event'} onChange={() => setMode('life_event')} />
          <span>When something happens in their life</span>
        </label>
      </fieldset>

      {mode === 'date' && (
        <label className="sched__field">
          <span className="sched__label">Deliver on</span>
          <input className="sched__input" type="date" min={minDate} value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
        </label>
      )}

      {mode === 'age' && (
        <>
          <label className="sched__field">
            <span className="sched__label">Deliver when they turn</span>
            <input className="sched__input" type="number" min={1} max={120} placeholder="21" value={age} onChange={(e) => setAge(e.target.value)} />
          </label>
          <p className="sched__note">
            Not a date — an age. Whenever that birthday arrives, this finds them. If you are recording this
            at the same age they will be when they hear it, they will meet you as an equal, not as an elder.
          </p>
        </>
      )}

      {mode === 'life_event' && (
        <>
          <label className="sched__field">
            <span className="sched__label">Deliver on</span>
            <select className="sched__input" value={lifeEvent} onChange={(e) => setLifeEvent(e.target.value as LifeEvent)}>
              {LIFE_EVENTS.map((e) => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
          </label>
          {!compoundMember && (
            <>
              <p className="sched__note">
                Some moments never come. Say what should happen then — so this is never simply lost.
              </p>
              <label className="sched__field">
                <span className="sched__label">But if it hasn’t happened by (optional)</span>
                <input className="sched__input" type="date" min={minDate} value={fallbackDate} onChange={(e) => setFallbackDate(e.target.value)} />
              </label>
              {fallbackDate !== '' && (
                <label className="sched__field">
                  <span className="sched__label">Then</span>
                  <select className="sched__input" value={fallbackAction} onChange={(e) => setFallbackAction(e.target.value as FallbackAction)}>
                    {FALLBACKS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </label>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
});
